import { useEffect, useRef, useState } from 'react';

/**
 * Ticks down to an absolute timestamp.
 *
 * Anchored to `Date.now()` rather than accumulating deltas, so a backgrounded
 * tab (or a phone that slept through a fleet's flight time) shows the correct
 * remaining time the instant it wakes — never a drifted one.
 *
 * @param target  Arrival/completion time. Accepts epoch ms, ISO string or Date.
 * @param onDone  Fired once when the countdown crosses zero.
 * @returns Remaining seconds, floored at 0.
 */
export function useCountdown(
  target: number | string | Date | null | undefined,
  onDone?: () => void,
): number {
  const targetMs = toMs(target);
  const [remaining, setRemaining] = useState(() => secondsUntil(targetMs));
  const doneRef = useRef(false);
  const onDoneRef = useRef(onDone);
  onDoneRef.current = onDone;

  useEffect(() => {
    doneRef.current = false;
    setRemaining(secondsUntil(targetMs));

    if (targetMs === null) return;

    const id = window.setInterval(() => {
      const next = secondsUntil(targetMs);
      setRemaining(next);
      if (next <= 0 && !doneRef.current) {
        doneRef.current = true;
        onDoneRef.current?.();
      }
    }, 1000);

    return () => window.clearInterval(id);
  }, [targetMs]);

  return remaining;
}

function toMs(target: number | string | Date | null | undefined): number | null {
  if (target === null || target === undefined) return null;
  if (target instanceof Date) return target.getTime();
  if (typeof target === 'number') return target;
  const parsed = Date.parse(target);
  return Number.isNaN(parsed) ? null : parsed;
}

function secondsUntil(targetMs: number | null): number {
  if (targetMs === null) return 0;
  return Math.max(0, Math.floor((targetMs - Date.now()) / 1000));
}
